import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ListingsMap.css';

/**
 * ListingsMap Component
 * 
 * Security & Privacy Features:
 * ✅ Uses Leaflet (open-source) instead of Google Maps
 * ✅ Uses OpenStreetMap tiles (community-driven, no API keys)
 * ✅ Uses Nominatim for geocoding (OSM's official service, secure)
 * ✅ No 3rd-party commercial map services
 * ✅ No API keys embedded in code
 * ✅ Respects Nominatim rate limits (1.5s between requests)
 * 
 * Performance Optimization (TODO):
 * - Cache geocoded coordinates in Firestore to avoid repeated API calls
 * - Add a 'geocodingStatus' field to listings collection
 * - On listing creation/update, pre-geocode in cloud function
 */

// Fix for default marker icons in react-leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface Listing {
  id: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  propertyType?: string;
  listPrice?: number;
  status?: string;
  latitude?: number;
  longitude?: number;
}

interface ListingsMapProps {
  height?: string;
}

const ListingsMap: React.FC<ListingsMapProps> = ({ height = '500px' }) => {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchListingsWithCoordinates();
  }, []);

  const fetchListingsWithCoordinates = async () => {
    try {
      setLoading(true);
      const listingsSnapshot = await getDocs(collection(db, 'listings'));
      const listingsData = listingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Listing[];

      // Filter listings that have valid location data
      const listingsWithLocation = listingsData.filter(listing => {
        // Check if listing has coordinates already
        if (listing.latitude && listing.longitude) {
          return true;
        }
        // Or has a valid zipCode
        if (listing.zipCode && listing.zipCode.length >= 5) {
          return true;
        }
        // Or has a complete address
        if (listing.address && listing.city && listing.state) {
          return true;
        }
        return false;
      });

      // Geocode addresses if needed with rate limiting
      const listingsWithCoords = await Promise.all(
        listingsWithLocation.map(async (listing, index) => {
          if (listing.latitude && listing.longitude) {
            return listing;
          }

          // Nominatim has usage policies - add delay between requests
          // Wait 1.5 seconds between geocoding requests to respect rate limits
          await new Promise(resolve => setTimeout(resolve, index * 1500));

          // Use Nominatim (OpenStreetMap) for geocoding - secure, open-source service
          try {
            const address = listing.address && listing.city && listing.state
              ? `${listing.address}, ${listing.city}, ${listing.state} ${listing.zipCode || ''}`
              : listing.zipCode
              ? `${listing.zipCode}, USA`
              : null;

            if (!address) return null;

            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 10000); // 10s timeout

            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&countrycodes=us`,
              {
                headers: {
                  'User-Agent': 'ScoreMyOffer/1.0 (+https://scoremyoffer.com)'
                },
                signal: controller.signal
              }
            );

            clearTimeout(timeout);

            if (!response.ok) {
              console.warn(`Geocoding service temporarily unavailable`);
              return null;
            }

            const data = await response.json();
            if (data && data.length > 0) {
              const result = {
                ...listing,
                latitude: parseFloat(data[0].lat),
                longitude: parseFloat(data[0].lon)
              };
              return result;
            }
          } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
              console.warn(`Geocoding timeout`);
            } else {
              console.error(`Error geocoding address:`, error);
            }
          }

          return null;
        })
      );

      // Filter out null results and set listings
      const validListings = listingsWithCoords.filter(
        (listing): listing is Listing => listing !== null && listing.latitude !== undefined && listing.longitude !== undefined
      );

      console.log(`✓ Successfully geocoded ${validListings.length} out of ${listingsData.length} listings`);
      if (validListings.length === 0) {
        setError('No listings with valid coordinates available');
      } else {
        setListings(validListings);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
      setError('Failed to load listings for map');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'sold': return '#6366f1';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="map-container" style={{ height }}>
        <div className="map-loading">
          <div className="spinner"></div>
          <p>Loading listings map...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="map-container" style={{ height }}>
        <div className="map-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="map-container" style={{ height }}>
        <div className="map-empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          <p>No listings with location data found</p>
          <span>Listings with valid addresses or zip codes will appear here</span>
        </div>
      </div>
    );
  }

  // Calculate center based on average of all coordinates
  const center: [number, number] = [
    listings.reduce((sum, l) => sum + (l.latitude || 0), 0) / listings.length,
    listings.reduce((sum, l) => sum + (l.longitude || 0), 0) / listings.length
  ];

  return (
    <div className="map-container" style={{ height }}>
      <MapContainer
        center={center}
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {listings.map((listing) => (
          <Marker
            key={listing.id}
            position={[listing.latitude!, listing.longitude!]}
          >
            <Popup>
              <div className="listing-popup">
                <h4>{listing.address || 'Address not available'}</h4>
                <p className="popup-location">
                  {listing.city}, {listing.state} {listing.zipCode}
                </p>
                {listing.propertyType && (
                  <p className="popup-type">Type: {listing.propertyType}</p>
                )}
                {listing.listPrice && (
                  <p className="popup-price">
                    ${listing.listPrice.toLocaleString()}
                  </p>
                )}
                {listing.status && (
                  <span
                    className="popup-status"
                    style={{ backgroundColor: getStatusColor(listing.status) }}
                  >
                    {listing.status}
                  </span>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default ListingsMap;
